import { Injectable } from '@angular/core';
import {
  LocalParkingFeeCalculationResult,
  StampAmountMapTableRow,
  StampBillableChargeTableResult,
  StampBillableChargeTableRow,
  StampBillableCustomerOption,
  StampDiscountOption,
  StampDiscountRateOption,
  StampDiscountUserOption,
  StampGroupDetail,
  StampGroupLineRow,
  StampGroupLogRow,
  StampGroupMemberRow,
  StampGroupTableRow,
  StampMemberDirectoryRow,
} from '../../models/parking/stamp-discount.model';

@Injectable({ providedIn: 'root' })
export class MapperStampDiscountService {
  mapGroupTableRow(dto: unknown): StampGroupTableRow {
    const r = dto as Record<string, unknown>;
    return {
      id: Number(r['id'] ?? r['Id'] ?? 0),
      name: (r['name'] ?? r['Name'] ?? null) as string | null,
      siteId: (r['siteId'] ?? r['SiteId'] ?? null) as number | null,
      siteName: (r['siteName'] ?? r['SiteName'] ?? null) as string | null,
      active: !!(r['active'] ?? r['Active'] ?? false),
      costType: String(r['costType'] ?? r['CostType'] ?? ''),
      cpCustomerId: (r['cpCustomerId'] ?? r['CpCustomerId'] ?? null) as number | null,
      cpCustomerName: (r['cpCustomerName'] ?? r['CpCustomerName'] ?? null) as string | null,
      hasLimit: !!(r['hasLimit'] ?? r['HasLimit'] ?? false),
      limitType: (r['limitType'] ?? r['LimitType'] ?? null) as string | null,
      limitQty: (r['limitQty'] ?? r['LimitQty'] ?? null) as number | null,
      lineCount: Number(r['lineCount'] ?? r['LineCount'] ?? 0),
      memberCount: Number(r['memberCount'] ?? r['MemberCount'] ?? 0),
      writeDate: (r['writeDate'] ?? r['WriteDate'] ?? null) as string | null,
    };
  }

  mapGroupTable(res: unknown): { data: StampGroupTableRow[]; total: number } {
    const raw = res as Record<string, unknown>;
    const list = (raw['data'] ?? raw['Data'] ?? []) as unknown[];
    return {
      data: list.map((item) => this.mapGroupTableRow(item)),
      total: Number(raw['total'] ?? raw['Total'] ?? 0),
    };
  }

  mapGroupDetail(dto: unknown): StampGroupDetail {
    const r = dto as Record<string, unknown>;
    const lines = (r['lines'] ?? r['Lines'] ?? []) as unknown[];
    const members = (r['members'] ?? r['Members'] ?? []) as unknown[];
    return {
      id: Number(r['id'] ?? r['Id'] ?? 0),
      name: (r['name'] ?? r['Name'] ?? null) as string | null,
      siteId: (r['siteId'] ?? r['SiteId'] ?? null) as number | null,
      siteName: (r['siteName'] ?? r['SiteName'] ?? null) as string | null,
      active: !!(r['active'] ?? r['Active'] ?? false),
      costType: String(r['costType'] ?? r['CostType'] ?? 'free'),
      cpCustomerId: (r['cpCustomerId'] ?? r['CpCustomerId'] ?? null) as number | null,
      cpCustomerName: (r['cpCustomerName'] ?? r['CpCustomerName'] ?? null) as string | null,
      hasLimit: !!(r['hasLimit'] ?? r['HasLimit'] ?? false),
      limitType: (r['limitType'] ?? r['LimitType'] ?? null) as string | null,
      limitQty: (r['limitQty'] ?? r['LimitQty'] ?? null) as number | null,
      remark: (r['remark'] ?? r['Remark'] ?? null) as string | null,
      lines: lines.map((item) => this.mapGroupLine(item)),
      members: members.map((item) => this.mapGroupMember(item)),
    };
  }

  mapGroupLine(dto: unknown): StampGroupLineRow {
    const r = dto as Record<string, unknown>;
    return {
      id: Number(r['id'] ?? r['Id'] ?? 0),
      groupId: Number(r['groupId'] ?? r['GroupId'] ?? 0),
      parkingRateId: Number(r['parkingRateId'] ?? r['ParkingRateId'] ?? 0),
      rateId: (r['rateId'] ?? r['RateId'] ?? null) as string | null,
      rateName: (r['rateName'] ?? r['RateName'] ?? null) as string | null,
      active: !!(r['active'] ?? r['Active'] ?? false),
      sequence: (r['sequence'] ?? r['Sequence'] ?? null) as number | null,
    };
  }

  mapGroupMember(dto: unknown): StampGroupMemberRow {
    const r = dto as Record<string, unknown>;
    return {
      id: Number(r['id'] ?? r['Id'] ?? 0),
      groupId: Number(r['groupId'] ?? r['GroupId'] ?? 0),
      targetUserId: Number(r['targetUserId'] ?? r['TargetUserId'] ?? 0),
      login: (r['login'] ?? r['Login'] ?? null) as string | null,
      stampAllowed: (r['stampAllowed'] ?? r['StampAllowed'] ?? null) as boolean | null,
      active: !!(r['active'] ?? r['Active'] ?? false),
      hasLimit: (r['hasLimit'] ?? r['HasLimit'] ?? null) as boolean | null,
      limitType: (r['limitType'] ?? r['LimitType'] ?? null) as string | null,
      limitQty: (r['limitQty'] ?? r['LimitQty'] ?? null) as number | null,
      usedQty: Number(r['usedQty'] ?? r['UsedQty'] ?? 0),
      usedMonthQty: Number(r['usedMonthQty'] ?? r['UsedMonthQty'] ?? 0),
      usagePeriod: (r['usagePeriod'] ?? r['UsagePeriod'] ?? null) as string | null,
    };
  }

  mapGroupLog(dto: unknown): StampGroupLogRow {
    const r = dto as Record<string, unknown>;
    return {
      id: Number(r['id'] ?? r['Id'] ?? 0),
      groupId: (r['groupId'] ?? r['GroupId'] ?? null) as number | null,
      memberId: (r['memberId'] ?? r['MemberId'] ?? null) as number | null,
      targetUserId: (r['targetUserId'] ?? r['TargetUserId'] ?? null) as number | null,
      targetLogin: (r['targetLogin'] ?? r['TargetLogin'] ?? null) as string | null,
      actorUid: (r['actorUid'] ?? r['ActorUid'] ?? null) as number | null,
      actorLogin: (r['actorLogin'] ?? r['ActorLogin'] ?? null) as string | null,
      action: String(r['action'] ?? r['Action'] ?? ''),
      parkingRateId: (r['parkingRateId'] ?? r['ParkingRateId'] ?? null) as number | null,
      costType: (r['costType'] ?? r['CostType'] ?? null) as string | null,
      qtyBefore: (r['qtyBefore'] ?? r['QtyBefore'] ?? null) as number | null,
      qtyAfter: (r['qtyAfter'] ?? r['QtyAfter'] ?? null) as number | null,
      limitType: (r['limitType'] ?? r['LimitType'] ?? null) as string | null,
      remark: (r['remark'] ?? r['Remark'] ?? null) as string | null,
      createDate: (r['createDate'] ?? r['CreateDate'] ?? null) as string | null,
    };
  }

  mapGroupLogs(res: unknown): { data: StampGroupLogRow[]; total: number } {
    const raw = res as Record<string, unknown>;
    const list = (raw['data'] ?? raw['Data'] ?? []) as unknown[];
    return {
      data: list.map((item) => this.mapGroupLog(item)),
      total: Number(raw['total'] ?? raw['Total'] ?? 0),
    };
  }

  mapAmountMapRow(dto: unknown): StampAmountMapTableRow {
    const r = dto as Record<string, unknown>;
    return {
      id: Number(r['id'] ?? r['Id'] ?? 0),
      siteId: Number(r['siteId'] ?? r['SiteId'] ?? 0),
      siteName: (r['siteName'] ?? r['SiteName'] ?? null) as string | null,
      amount: Number(r['amount'] ?? r['Amount'] ?? 0),
      parkingRateId: Number(r['parkingRateId'] ?? r['ParkingRateId'] ?? 0),
      rateId: (r['rateId'] ?? r['RateId'] ?? null) as string | null,
      rateName: (r['rateName'] ?? r['RateName'] ?? null) as string | null,
      name: (r['name'] ?? r['Name'] ?? null) as string | null,
      active: !!(r['active'] ?? r['Active'] ?? false),
      sequence: (r['sequence'] ?? r['Sequence'] ?? null) as number | null,
      remark: (r['remark'] ?? r['Remark'] ?? null) as string | null,
      writeDate: (r['writeDate'] ?? r['WriteDate'] ?? null) as string | null,
    };
  }

  mapAmountMapTable(res: unknown): { data: StampAmountMapTableRow[]; total: number } {
    const raw = res as Record<string, unknown>;
    const list = (raw['data'] ?? raw['Data'] ?? []) as unknown[];
    return {
      data: list.map((item) => this.mapAmountMapRow(item)),
      total: Number(raw['total'] ?? raw['Total'] ?? 0),
    };
  }

  mapMemberDirectoryRow(dto: unknown): StampMemberDirectoryRow {
    const r = dto as Record<string, unknown>;
    return {
      memberId: Number(r['memberId'] ?? r['MemberId'] ?? 0),
      targetUserId: Number(r['targetUserId'] ?? r['TargetUserId'] ?? 0),
      login: (r['login'] ?? r['Login'] ?? null) as string | null,
      stampAllowed: !!(r['stampAllowed'] ?? r['StampAllowed'] ?? false),
      groupId: Number(r['groupId'] ?? r['GroupId'] ?? 0),
      groupName: (r['groupName'] ?? r['GroupName'] ?? null) as string | null,
      groupActive: !!(r['groupActive'] ?? r['GroupActive'] ?? false),
      siteId: (r['siteId'] ?? r['SiteId'] ?? null) as number | null,
      siteName: (r['siteName'] ?? r['SiteName'] ?? null) as string | null,
      costType: String(r['costType'] ?? r['CostType'] ?? ''),
      cpCustomerId: (r['cpCustomerId'] ?? r['CpCustomerId'] ?? null) as number | null,
      cpCustomerName: (r['cpCustomerName'] ?? r['CpCustomerName'] ?? null) as string | null,
      discountRates: String(r['discountRates'] ?? r['DiscountRates'] ?? ''),
      groupHasLimit: !!(r['groupHasLimit'] ?? r['GroupHasLimit'] ?? false),
      groupLimitType: (r['groupLimitType'] ?? r['GroupLimitType'] ?? null) as string | null,
      groupLimitQty: (r['groupLimitQty'] ?? r['GroupLimitQty'] ?? null) as number | null,
      memberHasLimit: (r['memberHasLimit'] ?? r['MemberHasLimit'] ?? null) as boolean | null,
      memberLimitType: (r['memberLimitType'] ?? r['MemberLimitType'] ?? null) as string | null,
      memberLimitQty: (r['memberLimitQty'] ?? r['MemberLimitQty'] ?? null) as number | null,
      usedQty: Number(r['usedQty'] ?? r['UsedQty'] ?? 0),
      usedMonthQty: Number(r['usedMonthQty'] ?? r['UsedMonthQty'] ?? 0),
    };
  }

  mapMemberDirectoryTable(res: unknown): { data: StampMemberDirectoryRow[]; total: number } {
    const raw = res as Record<string, unknown>;
    const list = (raw['data'] ?? raw['Data'] ?? []) as unknown[];
    return {
      data: list.map((item) => this.mapMemberDirectoryRow(item)),
      total: Number(raw['total'] ?? raw['Total'] ?? 0),
    };
  }

  mapBillableChargeRow(dto: unknown): StampBillableChargeTableRow {
    const r = dto as Record<string, unknown>;
    return {
      id: Number(r['id'] ?? r['Id'] ?? 0),
      cpCustomerId: Number(r['cpCustomerId'] ?? r['CpCustomerId'] ?? 0),
      cpCustomerName: (r['cpCustomerName'] ?? r['CpCustomerName'] ?? null) as string | null,
      parkingTransactionId: Number(r['parkingTransactionId'] ?? r['ParkingTransactionId'] ?? 0),
      entryLicense: (r['entryLicense'] ?? r['EntryLicense'] ?? null) as string | null,
      cardCode: (r['cardCode'] ?? r['CardCode'] ?? null) as string | null,
      groupId: (r['groupId'] ?? r['GroupId'] ?? null) as number | null,
      groupName: (r['groupName'] ?? r['GroupName'] ?? null) as string | null,
      parkingRateId: (r['parkingRateId'] ?? r['ParkingRateId'] ?? null) as number | null,
      rateId: (r['rateId'] ?? r['RateId'] ?? null) as string | null,
      rateName: (r['rateName'] ?? r['RateName'] ?? null) as string | null,
      siteId: (r['siteId'] ?? r['SiteId'] ?? null) as number | null,
      siteName: (r['siteName'] ?? r['SiteName'] ?? null) as string | null,
      fee: Number(r['fee'] ?? r['Fee'] ?? 0),
      vat: Number(r['vat'] ?? r['Vat'] ?? 0),
      total: Number(r['total'] ?? r['Total'] ?? 0),
      chargeDate: (r['chargeDate'] ?? r['ChargeDate'] ?? null) as string | null,
      entryDate: (r['entryDate'] ?? r['EntryDate'] ?? null) as string | null,
      exitDate: (r['exitDate'] ?? r['ExitDate'] ?? null) as string | null,
      parkingDurationMinutes: (r['parkingDurationMinutes'] ?? r['ParkingDurationMinutes'] ?? null) as number | null,
      status: String(r['status'] ?? r['Status'] ?? ''),
      financeId: (r['financeId'] ?? r['FinanceId'] ?? null) as number | null,
      invoiceHeaderId: (r['invoiceHeaderId'] ?? r['InvoiceHeaderId'] ?? null) as number | null,
      remark: (r['remark'] ?? r['Remark'] ?? null) as string | null,
    };
  }

  mapBillableChargeTable(res: unknown): StampBillableChargeTableResult {
    const raw = res as Record<string, unknown>;
    const list = (raw['data'] ?? raw['Data'] ?? []) as unknown[];
    return {
      data: list.map((item) => this.mapBillableChargeRow(item)),
      total: Number(raw['total'] ?? raw['Total'] ?? 0),
      pendingMatchCount: Number(raw['pendingMatchCount'] ?? raw['PendingMatchCount'] ?? 0),
      pageParkingDurationMinutes: Number(raw['pageParkingDurationMinutes'] ?? raw['PageParkingDurationMinutes'] ?? 0),
      sumFee: Number(raw['sumFee'] ?? raw['SumFee'] ?? 0),
      sumVat: Number(raw['sumVat'] ?? raw['SumVat'] ?? 0),
      sumTotal: Number(raw['sumTotal'] ?? raw['SumTotal'] ?? 0),
    };
  }

  mapBillableCustomerOptions(res: unknown): { data: StampBillableCustomerOption[]; total: number } {
    const raw = res as Record<string, unknown>;
    const list = (Array.isArray(res) ? res : (raw['data'] ?? raw['Data'] ?? [])) as Record<string, unknown>[];
    return {
      data: list.map((r) => ({
        id: Number(r['id'] ?? r['Id'] ?? 0),
        label: String(r['label'] ?? r['Label'] ?? r['name'] ?? r['Name'] ?? ''),
        groupName: (r['groupName'] ?? r['GroupName'] ?? null) as string | null,
      })),
      total: Number(raw['total'] ?? raw['Total'] ?? list.length),
    };
  }

  formatParkingDuration(minutes: number | null | undefined): string {
    if (minutes == null || minutes < 0) return '—';
    const d = Math.floor(minutes / (60 * 24));
    const h = Math.floor((minutes % (60 * 24)) / 60);
    const m = minutes % 60;
    const parts: string[] = [];
    if (d > 0) parts.push(`${d} วัน`);
    if (h > 0) parts.push(`${h} ชม.`);
    parts.push(`${m} นาที`);
    return parts.join(' ');
  }

  statusLabelTh(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'pending': return 'รอดำเนินการ';
      case 'invoiced': return 'ออกใบแจ้งหนี้แล้ว';
      case 'cancelled': return 'ยกเลิก';
      default: return status || '—';
    }
  }

  mapRateOptions(res: unknown): StampDiscountRateOption[] {
    const list = Array.isArray(res) ? res : ((res as { data?: unknown[] })?.data ?? []);
    return (list as Record<string, unknown>[]).map((r) => ({
      id: Number(r['id'] ?? r['Id'] ?? 0),
      rateId: (r['rateId'] ?? r['RateId'] ?? null) as string | null,
      name: (r['name'] ?? r['Name'] ?? null) as string | null,
      siteId: (r['siteId'] ?? r['SiteId'] ?? null) as number | null,
    }));
  }

  mapCustomerOptions(res: unknown): { data: StampDiscountOption[]; total: number } {
    const raw = res as Record<string, unknown>;
    const list = (Array.isArray(res) ? res : (raw['data'] ?? raw['Data'] ?? [])) as Record<string, unknown>[];
    return {
      data: list.map((r) => ({
        id: Number(r['id'] ?? r['Id'] ?? 0),
        label: String(r['label'] ?? r['Label'] ?? r['name'] ?? r['Name'] ?? ''),
      })),
      total: Number(raw['total'] ?? raw['Total'] ?? list.length),
    };
  }

  mapUserOptionsTable(res: unknown): {
    data: StampDiscountUserOption[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = res as Record<string, unknown>;
    const list = (raw['data'] ?? raw['Data'] ?? (Array.isArray(res) ? res : [])) as Record<string, unknown>[];
    const data = list.map((r) => ({
      id: Number(r['id'] ?? r['Id'] ?? 0),
      login: (r['login'] ?? r['Login'] ?? null) as string | null,
      roleName: (r['roleName'] ?? r['RoleName'] ?? null) as string | null,
      stampAllowed: !!(r['stampAllowed'] ?? r['StampAllowed'] ?? false),
    }));
    const total = Number(raw['total'] ?? raw['Total'] ?? data.length);
    const page = Number(raw['page'] ?? raw['Page'] ?? 1);
    const pageSize = Number(raw['pageSize'] ?? raw['PageSize'] ?? 20);
    return {
      data,
      total,
      page,
      pageSize,
      totalPages: Number(raw['totalPages'] ?? raw['TotalPages'] ?? (total === 0 ? 0 : Math.ceil(total / pageSize))),
    };
  }

  /** @deprecated use mapUserOptionsTable */
  mapUserOptions(res: unknown): StampDiscountUserOption[] {
    return this.mapUserOptionsTable(res).data;
  }

  mapLocalParkingFeeCalculation(res: unknown): LocalParkingFeeCalculationResult {
    const r = (res ?? {}) as Record<string, unknown>;
    return {
      success: !!(r['success'] ?? r['Success']),
      message: String(r['message'] ?? r['Message'] ?? ''),
      transactionId: Number(r['transactionId'] ?? r['TransactionId'] ?? 0),
      siteId: Number(r['siteId'] ?? r['SiteId'] ?? 0),
      companyFee: Number(r['companyFee'] ?? r['CompanyFee'] ?? 0),
      parkingFee: Number(r['parkingFee'] ?? r['ParkingFee'] ?? 0),
      parkingOvernight: Number(r['parkingOvernight'] ?? r['ParkingOvernight'] ?? 0),
      penaltyFee: Number(r['penaltyFee'] ?? r['PenaltyFee'] ?? 0),
      totalPay: Number(r['totalPay'] ?? r['TotalPay'] ?? 0),
    };
  }
}
